import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { EyeBlueIcon } from "../../assets";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType } from "../../models";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../service/query";
import { ResultData } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { flattenObject } from "../../utils";
import { getDealListingRoute, getProjectDetailsByUuid } from "../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../widget";
import { IconCellFormatterData } from "../../widget/data/DataGrid/models";

interface UseYourTasksPanelReturnData {
  handleCloseWorkflowTaskPanel: () => void;
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useYourTasksPanel = (
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void,
  setShowWorkflowTaskPanel: (show: boolean) => void
): UseYourTasksPanelReturnData => {
  const navigate = useNavigate();
  const { user, currentUserType } = useAuth();

  const [dataIsLoading, setDataIsLoading] = useState(true);

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "taskLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 320,
      sortable: true,
    },
    {
      // "General" for a general task, otherwise the workflow object it belongs to, linked as before.
      name: "Type",
      key: "objectLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 160,
      sortable: true,
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "left",
      filterable: true,
      minWidth: 160,
      sortable: true,
    },
    {
      name: "Deadline",
      key: "deadlineDate",
      dataType: "string",
      formatter: "dateOnlyWithIndicator",
      alignment: "left",
      filterable: false,
      minWidth: 160,
      sortable: true,
    },
    {
      name: "Created at",
      key: "createdAt",
      dataType: "string",
      formatter: "dateOnly",
      alignment: "left",
      filterable: false,
      minWidth: 160,
      sortable: true,
    },
    {
      key: "viewTasksBoardIcon",
      name: "View",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 120,
    },
  ];

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData;

        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        const { workflowObjectUuid, workflowObjectType } = d;
        if (d.step == null) {
          /*
            A general task has no workflow, so the column states its type instead. Without an action the
            formatter renders plain text rather than a link, which is right — there is nothing to open.
          */
          result.objectLink = {
            text: TaskConstants.TASK_TYPE_GENERAL,
          } as DataGridButtonLinkCellFormatterData;
        } else if (workflowObjectUuid) {
          const objectRoute =
            workflowObjectType === "Deal"
              ? getDealListingRoute(workflowObjectUuid, currentUserType)
              : getProjectDetailsByUuid(workflowObjectUuid, currentUserType, { origin: Origin.DashboardPage });
          result.objectLink = {
            text: d.workflowObjectName,
            action: () => {
              navigate(objectRoute);
            },
          } as DataGridButtonLinkCellFormatterData;
        }

        result.viewTasksBoardIcon = {
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
          icon: <EyeBlueIcon width={20} height={20} strokeWidth={1.5} />,
        } as IconCellFormatterData;
        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ paging, sorting, filtering }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.assignee = {
      uuid: {
        operator: "eq",
        value: user?.userUuid || "",
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.objectLink) {
      filterCriteria.workflowObjectName = filterCriteria.objectLink;
      delete filterCriteria.objectLink;
    }

    const sortKeyMap: Record<string, string> = {
      taskLink: "name",
      objectLink: "workflowObjectName",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      filter: { results: filterCriteria },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    handleCloseWorkflowTaskPanel,
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
  };
};
